import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useNavigation } from '@react-navigation/native';
import i18n from 'i18n-js';

import { useAppDispatch } from '@store/hooks/useAppDispatch';
import { selectDashboardDateRangeSelection, setDashboardScope } from '@reducers/v2/dashboard/dashboard.slice';
import { DashboardOverviewScreenName, DashboardShowAllScreenName } from '@v2/pages/dashboard/navigation/config';
import { DashboardDrilldownScope } from '@v2/pages/dashboard/common/types/drilldown';
import { dashboardOverviewExtendedDateRangeLabels } from '@v2/pages/dashboard/modules/overview/components/DashboardOverview/components/DashboardOverviewHeader/components/DashboardOverviewDateRangePicker/constants';

import {
  DRILLDOWN_PARENT_SCOPE_PLURAL,
  DRILLDOWN_SCOPE_TO_DASHBOARD_SCOPE,
} from '@v2/pages/dashboard/modules/drilldown/components/DashboardDrilldownWrapper/components/DashboardDrilldownBreadcrumb/constants';
import { DashboardDrilldownBreadcrumbKey } from '@v2/pages/dashboard/modules/drilldown/components/DashboardDrilldownWrapper/components/DashboardDrilldownBreadcrumb/types';

export interface UseManageDashboardDrilldownBreadcrumbParams {
  scope: DashboardDrilldownScope;
  entityName?: string;
}

export interface UseManageDashboardDrilldownBreadcrumbResult {
  labelByKey: Record<DashboardDrilldownBreadcrumbKey, string>;
  onPressByKey: Partial<Record<DashboardDrilldownBreadcrumbKey, () => void>>;
}

export const useManageDashboardDrilldownBreadcrumb = ({
  scope,
  entityName,
}: UseManageDashboardDrilldownBreadcrumbParams): UseManageDashboardDrilldownBreadcrumbResult => {
  const dispatch = useAppDispatch();
  const navigation = useNavigation<any>();
  const { dateRange } = useSelector(selectDashboardDateRangeSelection);

  const onPressDateRange = useCallback(() => {
    navigation.navigate(DashboardOverviewScreenName as never);
  }, [navigation]);

  const onPressParentScope = useCallback(() => {
    const dashboardScope = DRILLDOWN_SCOPE_TO_DASHBOARD_SCOPE[scope];
    dispatch(setDashboardScope(dashboardScope));
    navigation.navigate(DashboardShowAllScreenName as never, { scope: dashboardScope } as never);
  }, [dispatch, navigation, scope]);

  const labelByKey: Record<DashboardDrilldownBreadcrumbKey, string> = {
    [DashboardDrilldownBreadcrumbKey.DateRange]: dashboardOverviewExtendedDateRangeLabels[dateRange] ?? '',
    [DashboardDrilldownBreadcrumbKey.ParentScope]: i18n.t(DRILLDOWN_PARENT_SCOPE_PLURAL[scope]),
    [DashboardDrilldownBreadcrumbKey.Entity]: entityName ?? '',
  };

  const onPressByKey: Partial<Record<DashboardDrilldownBreadcrumbKey, () => void>> = {
    [DashboardDrilldownBreadcrumbKey.DateRange]: onPressDateRange,
    [DashboardDrilldownBreadcrumbKey.ParentScope]: onPressParentScope,
  };

  return { labelByKey, onPressByKey };
};
